import { GetterTree } from 'vuex';
import { RootState } from '@/store';
import { State } from './state';
import { FormatOptions, UserHierarchy, UserPreferences } from '@/types/Global';
import { get, map } from 'lodash';
import { Option } from '@/types/Client';
import { formatDataValue } from '@/helpers/format';
import { DataType, NameResolutionType } from '@/enums/Global';
import { LeadDataResolution } from './types';

export type Getters = {
  getUserHierarchy(state: State): UserHierarchy;
  getOptions(state: State, field: string): Array<Option>;
  getEtlUpdateDate(state: State): string;
  getShowSessionTimeoutModalState(state: State): boolean;
  getUserPreferences(state: State): UserPreferences;
  getNameFromNameResolutionMap(
    state: State,
    params: { id: string; type: NameResolutionType }
  ): string | undefined;
  getNameResolutionIsLoading(state: State, type: NameResolutionType): boolean;
  getLeadData(state: State, leadId: string): LeadDataResolution | undefined;
  getClientIdsByNameSearch(state: State, searchTerm: string): Array<string>;
  getLeadIdsByNameSearch(state: State, searchTerm: string): Array<string>;
};

export const getters: GetterTree<State, RootState> & Getters = {
  getUserHierarchy: state => {
    const userHierarchy = get(state, 'userHierarchy', null);

    if (userHierarchy) {
      return {
        bankerId: userHierarchy.bankerId,
        marketId: userHierarchy.marketId,
        teamId: userHierarchy.teamId,
      } as UserHierarchy;
    }
    return {} as UserHierarchy;
  },
  getUserPreferences: state => {
    const userPreferences = get(state, 'userPreferences', null);

    if (userPreferences) {
      return {
        viewHistoricPricing: formatDataValue(
          userPreferences.viewHistoricPricing
        ),
      } as UserPreferences;
    }
    return {} as UserPreferences;
  },
  getOptions: (state, field): Option[] => {
    const options = get(state, field, []) as string[];
    return map(options, (option: string): Option => {
      return {
        value: {
          dataValue: option,
          dataType: DataType.STRING,
        },
        display: {
          dataValue: option,
          dataType: DataType.STRING,
        },
        additionalDisplay: {
          dataValue: '',
          dataType: DataType.STRING,
        },
        isStandard: false,
      };
    });
  },
  getEtlUpdateDate: state => {
    const etlState = get(state, 'etl', null);
    return etlState && etlState.updateDate
      ? (formatDataValue(
          { dataValue: etlState.updateDate, dataType: DataType.DATETIME },
          {
            useFromNow: true,
            showTime: true,
            overrideDateFormat: true,
          } as FormatOptions
        ) as string)
      : '';
  },
  getShowSessionTimeoutModalState: state => {
    return state.showSessionModal;
  },
  getNameFromNameResolutionMap: (state, params) => {
    switch (params.type) {
      case NameResolutionType.CLIENT:
        return (
          state.clientNameResolutionMap[params.id] &&
          state.clientNameResolutionMap[params.id].trim()
        );
      case NameResolutionType.CONTAINER:
        return (
          state.containerNameResolutionMap[params.id] &&
          state.containerNameResolutionMap[params.id].trim()
        );
      case NameResolutionType.COLLECTION:
        return (
          state.collectionNameResolutionMap[params.id] &&
          state.collectionNameResolutionMap[params.id].trim()
        );
      case NameResolutionType.LEAD:
        return (
          state.leadsDataResolutionMap[params.id] &&
          `${state.leadsDataResolutionMap[
            params.id
          ].Firstname.trim()} ${state.leadsDataResolutionMap[
            params.id
          ].Lastname.trim()}`
        );
      case NameResolutionType.LEAD_INTERMEDIARY:
        return (
          state.leadsDataResolutionMap[params.id] &&
          state.leadsDataResolutionMap[params.id].IntermediaryName.trim()
        );
      case NameResolutionType.BANKER: {
        const banker = state.bankerNameResolutionMap.find(
          banker => banker.banker_id === params.id
        );
        // if banker_id maps to a banker, show their name, else show the banker_id, else show Unknown Advisor
        return banker
          ? banker.name.trim()
          : params.id
          ? params.id.trim()
          : 'Unknown Advisor';
      }
      default:
        return undefined;
    }
  },
  getNameResolutionIsLoading: (state, type) => {
    switch (type) {
      case NameResolutionType.CLIENT:
        return state.clientNameResolutionLoading;
      case NameResolutionType.CONTAINER:
        return state.containerNameResolutionLoading;
      case NameResolutionType.COLLECTION:
        return state.collectionNameResolutionLoading;
      case NameResolutionType.LEAD:
        return state.leadsDataResolutionLoading;
      case NameResolutionType.LEAD_INTERMEDIARY:
        return state.leadsDataResolutionLoading;
      case NameResolutionType.BANKER:
        return state.bankerNameResolutionLoading;
      default:
        return false;
    }
  },
  getLeadData: (state, leadId) => {
    if (state.leadsDataResolutionMap[leadId]) {
      return state.leadsDataResolutionMap[leadId];
    }
  },
  getClientIdsByNameSearch: (state, searchTerm) => {
    const ids: string[] = [searchTerm];

    for (const key in state.clientNameResolutionMap) {
      if (
        state.clientNameResolutionMap[key]
          .trim()
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      ) {
        ids.push(key);
      }
    }

    return ids;
  },
  getLeadIdsByNameSearch: (state, searchTerm) => {
    const ids: string[] = [searchTerm];

    for (const key in state.leadsDataResolutionMap) {
      const leadFullName = `${state.leadsDataResolutionMap[key].Firstname} ${state.leadsDataResolutionMap[key].Lastname}`;
      if (
        leadFullName
          .trim()
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      ) {
        ids.push(key);
      }
    }

    return ids;
  },
};
